import { kurzform, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Schichtangabe } from './typen'

/**
 * Wie eine Wertspalte heißt (R777, R778).
 *
 * **Eine Stelle, und alle fragen sie.** Bis zum 2026-09-26 führten der
 * Bilanznachweis und der Nachweis der Kapitalflußrechnung je eine eigene
 * Tabelle — zeichengleich, und damit zwei Gelegenheiten, dieselbe Spalte
 * verschieden zu benennen.
 *
 * **Die Endspalte ist der Grund, aus dem es diese Datei gibt.** Sie trägt den
 * Namen ihrer Bewertungsebene und nicht mehr das feste Wort *Konzern*: Die
 * Konsolidierungssachverhalte sind ein Typ derselben Dimension und werden in
 * derselben Gruppe aggregiert (Auftraggeber, 2026-09-26). Wer die Gruppe von
 * *HGB* nach *Handelsrecht* umbenennt, sieht es überall.
 */
const FEST: Record<string, Sprachschluessel> = {
  hb2: 'spalte.hb2',
  kapko: 'spalte.kapko',
  schuko: 'spalte.schuko',
  aeko: 'spalte.aeko',
  zwerge_uv: 'spalte.zwerge_uv',
  zwerge_av: 'spalte.zwerge_av',
  konzern: 'spalte.konzern',
  // Die Konsolidierungsspalte des Strukturbilds (R829) — sie steht nicht an
  // der Faktenzeile, trägt aber seit dem 2026-09-27 ihren Nachweis, und
  // dessen Kopf nennt sie beim Namen wie jede andere.
  konsolidierung: 'spalte.konsolidierung',
}

/**
 * Der Name einer Wertspalte, in der Sprache des Anwenders.
 *
 * `gruppe` ist die Bewertungsebene des Berichts, aus dem die Zelle stammt.
 * Steht sie, trägt die Endspalte ihren Namen; fehlt sie — etwa in einem
 * Bericht, der vor dieser Änderung entstanden ist —, bleibt es beim festen
 * Wort.
 *
 * **Ein unbekannter Schlüssel wird durchgereicht, nicht verschluckt.** Eine
 * Schichtspalte heißt `hb2:LDG-ROH` und steht heute roh da; das ist besser als
 * eine leere Zelle und ein Befund für sich.
 */
/**
 * Der Vorsatz einer **Schichtspalte** — `hb2:LDG-ROH` ist die Handelsbilanz II,
 * eingegrenzt auf die Rohdaten (R402).
 *
 * **Ein Muster statt einer Aufzählung**, weil die Schichten der Anwender
 * anlegt. Der Vorsatz steht im Rechenkern genauso; zwei Schreibweisen wären
 * zwei Gelegenheiten, ihn verschieden zu lesen.
 */
const SCHICHTVORSATZ = 'hb2:'

export function spaltenname(
  schluessel: string | null,
  gruppe?: Schichtangabe | null,
  schichten?: Schichtangabe[],
): string | null {
  if (schluessel === null) {
    return null
  }

  if (schluessel === 'konzern' && gruppe) {
    return kurzform(gruppe.kuerzel, gruppe.namen) || gruppe.code
  }

  /*
    **Eine Schichtspalte sagt zweierlei** (R402, Befund vom 2026-09-26): die
    Konsolidierungsspalte und die Bewertungsebene darin. Bis dahin stand sie
    roh da — `hb2:LDG-ROH` —, und der Anwender las eine Kennung statt zweier
    Namen.

    **Ohne den Schichtkatalog bleibt der Schlüssel stehen**, nicht die halbe
    Auskunft: Eine Spalte, die nur *HB II* hieße, verschwiege gerade das, was
    sie von der Spalte daneben unterscheidet.
  */
  if (schluessel.startsWith(SCHICHTVORSATZ)) {
    const schicht = schluessel.slice(SCHICHTVORSATZ.length)
    const gepflegt = (schichten ?? []).find((s) => s.schluessel === schicht)

    return gepflegt
      ? `${t('spalte.hb2')} · ${kurzform(gepflegt.kuerzel, gepflegt.namen) || gepflegt.code}`
      : schluessel
  }

  return FEST[schluessel] ? t(FEST[schluessel]!) : schluessel
}

/**
 * Der Name einer **einzelnen Bewertungsebene** — der Schicht, aus der ein
 * Betrag kommt (R777, Auftrag vom 2026-09-26).
 *
 * **Oben die Gruppe, in der Zeile die Schicht.** Erst daran sieht der
 * Anwender, was Rohdaten sind und was eine Anpassung: *Roh* oder
 * *Delta HGB*, statt zweimal *HB II*.
 *
 * **Ohne Schicht die Konsolidierungsspalte.** Eine Konsolidierungsbuchung
 * entsteht oberhalb der Einzelabschlüsse und gehört keiner Schicht an; dort
 * ist *KapKo* die feinste Bewertungsebene, die die Zeile trägt.
 */
export function schichtname(
  schicht: string | null | undefined,
  spalte: string | null,
  schichten: Schichtangabe[] | undefined,
  gruppe?: Schichtangabe | null,
): string | null {
  if (!schicht) {
    return spaltenname(spalte, gruppe, schichten)
  }

  const gepflegt = (schichten ?? []).find((s) => s.schluessel === schicht)
  return gepflegt ? kurzform(gepflegt.kuerzel, gepflegt.namen) || gepflegt.code : schicht
}
